import { FC, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { AccessLevel, UserApiKeyDataSerialized, UserApiKeySerialized } from '@playq/octopus2-auth';
import { TextFilter, TimeFilter } from '@playq/octopus-common';
import { TgDuration } from '@playq/services-shared';

import { useTableQueryChange } from '/hooks';
import { services2 } from '/api';
import { formatDate, throwGenericFailure } from '/helpers';
import { snackbarService } from '/common/snackbarService';
import { DeleteIconStyled as DeleteIcon } from '/shared/Tree/plugins/styles';
import { confirmDialog, ConfirmDialogType } from '/common/ConfirmDialog';
import { IGenericTableEntity } from '/common/models';

import { ApiKeysManagerContent } from './ApiKeysManagerContent';
import {
  ApiKeyDataToCopy,
  ApiKeysQueryFields,
  ExistingApiKeyIdBeingProcessed,
  IApiKeyCreationMetadata,
  IIndependentApiKeysTableContentMetadata,
  IKeyCreationProcessingConfig,
} from './types';

const defaultAccessLevelValue = AccessLevel.Read;

export const ApiKeysManager: FC = () => {
  const [apiKeysData, setApiKeysData] = useState<UserApiKeyDataSerialized[]>([]);
  const [tableEntries, setTableEntries] = useState<UserApiKeyDataSerialized[]>([]);
  const [existingKeyIdBeingProcessed, setExistingKeyIdBeingProcessed] = useState<ExistingApiKeyIdBeingProcessed>(null);
  const [keyDataToCopy, setKeyDataToCopy] = useState<ApiKeyDataToCopy>(null);
  const [keyCreationMetadata, setKeyCreationMetadata] = useState<IApiKeyCreationMetadata>({
    isKeyCreationBeingProcessed: false,
    isKeyRegenerationBeingProcessed: false,
    newKeyName: '',
    newTtl: undefined,
    newKeyAccessLevel: defaultAccessLevelValue,
    tableQueryToPerformAfterKeyCreation: null,
    tableQueryToPerformAfterKeyRegeneration: null,
    id: undefined,
  });
  const [isTableProcessing, setIsTableProcessing] = useState<boolean>(false);

  const independentTableContentMetadata: IIndependentApiKeysTableContentMetadata = {
    columns: [
      {
        label: 'ID',
        render: (entry: UserApiKeyDataSerialized) => entry.id,
        sortField: ApiKeysQueryFields.ID,
        filterField: ApiKeysQueryFields.ID,
        filterType: TextFilter.ClassName,
      },
      {
        label: 'Name',
        render: (entry: UserApiKeyDataSerialized) => entry.name,
        sortField: ApiKeysQueryFields.Name,
        filterField: ApiKeysQueryFields.Name,
        filterType: TextFilter.ClassName,
      },
      {
        label: 'Created At',
        render: (entry: UserApiKeyDataSerialized) => formatDate(entry.created),
        sortField: ApiKeysQueryFields.CreatedAt,
        filterField: ApiKeysQueryFields.CreatedAt,
        filterType: TimeFilter.ClassName,
      },
      {
        label: 'Last Used At',
        render: (entry: UserApiKeyDataSerialized) => {
          const { lastUsed } = entry;

          return !lastUsed ? '' : formatDate(lastUsed);
        },
        sortField: ApiKeysQueryFields.LastUsedAt,
        filterField: ApiKeysQueryFields.LastUsedAt,
        filterType: TimeFilter.ClassName,
      },
      {
        label: 'TTL',
        render: ({ ttl }: UserApiKeyDataSerialized) => (ttl ? `${ttl.length} ${ttl.unit.toLowerCase()}` : undefined),
        sortField: ApiKeysQueryFields.TTL,
        filterField: ApiKeysQueryFields.TTL,
        filterType: TextFilter.ClassName,
      },
      {
        label: 'Expiration',
        render: ({ expiration }: UserApiKeyDataSerialized) => (expiration ? formatDate(expiration) : undefined),
        sortField: ApiKeysQueryFields.Expiration,
        filterField: ApiKeysQueryFields.Expiration,
        filterType: TimeFilter.ClassName,
      },
    ],
    rowKeyResolver: (entry: UserApiKeyDataSerialized) => entry.id,
  };

  const handleTableEntriesSetting = (data: IGenericTableEntity[]) => {
    setTableEntries(data as unknown as UserApiKeyDataSerialized[]);
  };

  const [, setTableQuery] = useTableQueryChange({
    tableData: apiKeysData as unknown as IGenericTableEntity[],
    tableEntriesSettingHandler: handleTableEntriesSetting,
    isTableProcessingSettingHandler: setIsTableProcessing,
  });

  const dependentTableContentMetadata = useMemo(
    () => ({
      data: tableEntries,
      rowActions: [
        {
          label: 'Delete',
          icon: DeleteIcon,
          tooltip: (entry: UserApiKeyDataSerialized) => `Delete ${entry.name} `,
          pending: (entry: UserApiKeyDataSerialized) => entry.id === existingKeyIdBeingProcessed,
          onClick: (_event: SyntheticEvent, entry: UserApiKeyDataSerialized) => {
            const entryName: string = entry.name;

            confirmDialog({
              type: ConfirmDialogType.Delete,
              entityType: 'api-key',
              entityName: entryName,
              onSuccess: () => deleteApiKey(entry.id),
            });
          },
        },
      ],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [existingKeyIdBeingProcessed, tableEntries]
  );

  const handleApiKeysListUpdate = useCallback((keyId: number) => {
    setApiKeysData((prevApiKeysData) => {
      const currentApiKeysData: UserApiKeyDataSerialized[] = prevApiKeysData.slice();
      const deletedApiKeyDataIndex: number = prevApiKeysData.findIndex(
        (entry: UserApiKeyDataSerialized): boolean => entry.id === keyId
      );

      currentApiKeysData.splice(deletedApiKeyDataIndex, 1);

      return currentApiKeysData;
    });

    setTableEntries((prevTableEntries) => {
      const currentTableEntries: UserApiKeyDataSerialized[] = prevTableEntries.slice();
      const deletedTableEntryIndex: number = prevTableEntries.findIndex(
        (entry: UserApiKeyDataSerialized): boolean => entry.id === keyId
      );

      currentTableEntries.splice(deletedTableEntryIndex, 1);

      return currentTableEntries;
    });

    setExistingKeyIdBeingProcessed(null);
  }, []);

  const deleteApiKey = (keyId: number) => {
    setExistingKeyIdBeingProcessed(keyId);

    services2.userProfileService
      .deleteApiKey(keyId)
      .then((data) => {
        data.bifold(
          () => {
            handleApiKeysListUpdate(keyId);
          },
          (err) => throwGenericFailure(err)
        );
      })
      .catch((err: Error) => {
        snackbarService.error(`delete api key -${err.message}`);
      });
  };

  const toggleKeyCreationProcessing = useCallback((config: IKeyCreationProcessingConfig) => {
    const {
      isCreationBeingProcessed,
      isKeyRegenerationBeingProcessed,
      keyName = '',
      keyAccessLevel = defaultAccessLevelValue,
      tableQuery,
      ttl,
      id,
    }: IKeyCreationProcessingConfig = config;

    setKeyCreationMetadata({
      isKeyRegenerationBeingProcessed,
      isKeyCreationBeingProcessed: isCreationBeingProcessed,
      newKeyName: keyName,
      newTtl: ttl,
      newKeyAccessLevel: keyAccessLevel,
      tableQueryToPerformAfterKeyCreation: tableQuery || null,
      tableQueryToPerformAfterKeyRegeneration: tableQuery || null,
      id,
    });
  }, []);

  const createApiKey = useCallback(
    (keyName: string, accessLevel: AccessLevel, ttl?: TgDuration) => {
      services2.userProfileService
        .createApiKey(keyName, accessLevel, ttl, undefined)
        .then((data) => {
          data.bifold(
            (res) => {
              setKeyDataToCopy(res.serialize() ?? null);
            },
            (err) => throwGenericFailure(err)
          );
        })
        .catch((err: Error) => {
          snackbarService.error(`create api key -${err.message}`);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableEntries]
  );

  const regenerateApiKey = useCallback(
    (keyId: number, ttl?: TgDuration) => {
      services2.userProfileService
        .regenerateApiKey(keyId, ttl, undefined)
        .then((data) => {
          data.bifold(
            (res) => {
              setKeyDataToCopy(res.serialize() ?? null);
              handleApiKeysListUpdate(keyId);
            },
            (err) => throwGenericFailure(err)
          );
        })
        .catch((err: Error) => {
          snackbarService.error(`regenerate api key -${err.message}`);
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tableEntries]
  );

  const handleApiKeyFormSubmission = useCallback(
    ({
      keyName,
      keyAccessLevel,
      ttl,
      tableQuery,
      isCreateMode,
      id,
    }: Omit<IKeyCreationProcessingConfig, 'isCreationBeingProcessed' | 'isKeyRegenerationBeingProcessed'> & {
      isCreateMode: boolean;
    }) => {
      toggleKeyCreationProcessing({
        isCreationBeingProcessed: isCreateMode,
        isKeyRegenerationBeingProcessed: !isCreateMode,
        keyName,
        ttl,
        keyAccessLevel,
        tableQuery,
        id,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleApiKeyObtainment = useCallback((keysSerialized: UserApiKeyDataSerialized[]) => {
    if (!Array.isArray(keysSerialized)) {
      return;
    }

    resetApiKeysTableData(keysSerialized);
  }, []);

  const obtainApiKeyTableEntries = useCallback(() => {
    setIsTableProcessing(true);

    services2.userProfileService
      .getApiKeys()
      .then((data) => {
        data.bifold(
          (res) => {
            handleApiKeyObtainment(res?.serialize().keys);
            setIsTableProcessing(false);
          },
          (err) => throwGenericFailure(err)
        );
      })
      .catch((err: Error) => {
        snackbarService.error(`retrieve user api keys -${err.message}`);
      });
  }, [handleApiKeyObtainment]);

  const handleApiKeyCreationDialogFadingOut = useCallback(() => {
    setKeyDataToCopy(null);
    setExistingKeyIdBeingProcessed(null);
  }, []);

  const resetApiKeysTableData = (
    newKeysData: UserApiKeyDataSerialized[],
    newTableEntries?: UserApiKeyDataSerialized[]
  ) => {
    setApiKeysData(newKeysData);
    setTableEntries(newTableEntries || newKeysData.slice());
  };

  const {
    isKeyCreationBeingProcessed,
    isKeyRegenerationBeingProcessed,
    newKeyName,
    newTtl,
    newKeyAccessLevel,
    tableQueryToPerformAfterKeyCreation,
    id,
  } = keyCreationMetadata;

  useEffect(() => {
    obtainApiKeyTableEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isKeyCreationBeingProcessed) {
      createApiKey(newKeyName, newKeyAccessLevel, newTtl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKeyCreationBeingProcessed]);

  useEffect(() => {
    if (isKeyRegenerationBeingProcessed && id !== undefined) {
      regenerateApiKey(id, newTtl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKeyCreationBeingProcessed, id, newTtl]);

  useEffect(() => {
    if (keyDataToCopy) {
      const { key: _key, ...apiKeyData }: UserApiKeySerialized = keyDataToCopy;

      setApiKeysData(apiKeysData.concat(apiKeyData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyDataToCopy]);

  useEffect(() => {
    if ((isKeyCreationBeingProcessed || isKeyRegenerationBeingProcessed) && tableQueryToPerformAfterKeyCreation) {
      setTableQuery(tableQueryToPerformAfterKeyCreation);
      toggleKeyCreationProcessing({ isCreationBeingProcessed: false, isKeyRegenerationBeingProcessed: false });

      const messageAction = isKeyCreationBeingProcessed
        ? `Key for ${keyDataToCopy?.name || ''} successfully re-created.`
        : `Key ${keyDataToCopy?.name || ''} successfully created.`;
      snackbarService.success(messageAction);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKeysData]);

  return (
    <ApiKeysManagerContent
      keysData={apiKeysData}
      isKeyCreationBeingProcessed={isKeyCreationBeingProcessed}
      isKeyRegenerationBeingProcessed={isKeyRegenerationBeingProcessed}
      isTableProcessing={isTableProcessing}
      tableContentMetadata={{
        ...dependentTableContentMetadata,
        ...independentTableContentMetadata,
      }}
      keyDataToCopy={keyDataToCopy}
      defaultKeyAccessLevel={defaultAccessLevelValue}
      handleApiKeyFormSubmission={handleApiKeyFormSubmission}
      keyCreationDialogFadingOutHandler={handleApiKeyCreationDialogFadingOut}
      tableEntriesSettingHandler={handleTableEntriesSetting}
      isTableProcessingSettingHandler={setIsTableProcessing}
    />
  );
};
